/**
 * "Set the city photo from an approved venue photo."
 *
 * Evan (2026-08-01): "On city pages in the admin dashboard I need to be able to select from
 * approved venue photos a photo to be the city photo."
 *
 * ─── WRITE PATH (verified against production, 2026-08-01) ────────────────────────────────────
 * `public.cities` has RLS ENABLED and exactly ONE policy: `cities_read_public` (SELECT, `true`).
 * There is NO INSERT/UPDATE/DELETE policy, so a direct `from('cities').update(...)` from this
 * dashboard (anon key + the admin's JWT) matches no rows and silently succeeds with 0 rows
 * changed — the same trap `VenueEditModal` documents for `venues`. Every write here therefore
 * goes through the existing SECURITY DEFINER RPC `admin_approve_city_photo(p_photo_id uuid)`,
 * which asserts the caller is an admin and then sets
 * `cities.hero_image_url` + `cities.google_fetch_disabled = true`.
 *
 * That RPC keys off a `city_photos` row, so applying a venue photo is:
 *   1. re-encode the chosen photo to JPEG (optionally through the crop modal),
 *   2. upload it to the `city-heroes` bucket under `user-submissions/<cityId>/…`,
 *   3. INSERT a `city_photos` row pointing at that object,
 *   4. call `admin_approve_city_photo` on it.
 *
 * NO new migration is required — steps 3 and 4 are both already permitted:
 *   - `city_photos` INSERT = permissive `city_photos_insert_own` (`auth.uid() = user_id`)
 *     AND restrictive `city_photos_require_verified_active_ins` (`can_write()`). Both admin
 *     accounts (`evan`, `jk`) are phone-confirmed and unsuspended, so `can_write()` passes.
 *   - `city_photos.source` carries CHECK `(source = 'city_upload')` — that literal is the ONLY
 *     accepted value, hence `CITY_PHOTO_SOURCE` below.
 *
 * Step 2 (rather than pointing the city at the venue photo's own URL) is deliberate: the venue
 * review flow DELETES a venue photo's storage object when an admin re-crops it
 * (`VenueReviewScreen.approvePhoto` → `storage.from('venue-photos').remove`). A city hero
 * borrowing that URL would 404 later. Copying the bytes makes the hero self-owned.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Area } from 'react-easy-crop';
import { supabase } from '../lib/supabase';
import { cropImageToJpegBlob, resolveVenuePhotoCropUrl } from '../lib/cropImage';
import { uploadCityHeroBlob } from '../lib/cityPhotoCrop';
import { useEscToClose } from '../lib/useEscToClose';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';
import { VenuePhotoCropModal } from './VenuePhotoCropModal';

/**
 * The app renders a city hero SQUARE — `screens/city.tsx:3565` sets
 * `style={{ width: "100%", aspectRatio: 1 }}` with `contentFit="cover"` (Evan, 2026-08-01:
 * "SQUARE, like every other photo frame in the app"), and the city GRID card hero is
 * `aspectRatio: 1` too (`screens/city.tsx:458`). So the default crop is 1:1; anything wider is
 * center-cropped on device and the admin loses the framing they picked. Approved venue photos
 * are shown square in the app as well (`screens/venue-discovery.tsx:503`), which is why a venue
 * photo needs no re-crop to work as a city hero — the cropper is offered for FRAMING only.
 *
 * The crop modal's own `aspect` default is NOT relied on — it is always passed explicitly.
 */
export const CITY_HERO_CROP_ASPECT = 1;

/** `city_photos_source_check` allows this literal and nothing else. */
const CITY_PHOTO_SOURCE = 'city_upload';

export type CityVenuePhotoOption = {
  id: string;
  venue_id: string;
  venue_name: string | null;
  public_url: string | null;
  created_at: string;
};

function rpcMissing(message: string, fn: string): boolean {
  const m = message.toLowerCase();
  return m.includes(fn.toLowerCase()) || m.includes('42883') || m.includes('could not find the function');
}

/** Natural pixel size of the source image — used to crop "the whole frame" when not re-framing. */
function imageNaturalSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      } else {
        reject(new Error('Could not read the photo dimensions'));
      }
    };
    img.onerror = () => reject(new Error('Could not load the photo'));
    img.src = src;
  });
}

export function CityHeroPickerModal({
  open,
  cityId,
  cityName,
  currentHeroUrl,
  adminUserId,
  photos,
  loading,
  error,
  onClose,
  onApplied,
}: {
  open: boolean;
  cityId: string;
  cityName: string | null;
  currentHeroUrl: string | null;
  adminUserId: string;
  /**
   * Approved venue photos for venues in this city. Loaded ONCE by the Cities page when a city is
   * opened (it also needs the count for the button badge), then handed down — so opening this
   * modal costs zero extra requests.
   */
  photos: CityVenuePhotoOption[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  /** Fires after `cities.hero_image_url` is committed, with the new URL. */
  onApplied: (heroUrl: string) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  useEscToClose(open && !cropOpen && !busy, onClose);

  // Reset per-open so a previous city's selection never leaks into the next one.
  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setApplyError(null);
      setCropOpen(false);
      setCropSourceUrl(null);
    }
  }, [open]);

  const selected = useMemo(
    () => (selectedId ? photos.find((p) => p.id === selectedId) ?? null : null),
    [photos, selectedId],
  );

  /**
   * Commit `blob` as this city's hero. Single funnel for both the framed and the full-frame
   * path — see the file header for why each step exists.
   */
  const commitHero = useCallback(
    async (blob: Blob, sourcePhotoId: string) => {
      const uploadPath = `user-submissions/${cityId}/venue-photo-${sourcePhotoId}-${Date.now()}.jpg`;
      const { publicUrl, storagePath } = await uploadCityHeroBlob(uploadPath, blob);

      const { data: inserted, error: insErr } = await supabase
        .from('city_photos')
        .insert({
          city_id: cityId,
          user_id: adminUserId,
          storage_path: storagePath,
          public_url: publicUrl,
          source: CITY_PHOTO_SOURCE,
          approved: false,
          rejected: false,
        })
        .select('id')
        .single();
      if (insErr) throw insErr;
      const photoRowId = (inserted as { id: string } | null)?.id;
      if (!photoRowId) throw new Error('Could not create the city photo row');

      const { data: approvedRows, error: rpcErr } = await supabase.rpc('admin_approve_city_photo', {
        p_photo_id: photoRowId,
      });
      if (rpcErr) {
        if (rpcMissing(rpcErr.message, 'admin_approve_city_photo')) {
          throw new Error(
            'City photo approval is not set up yet. Run supabase/sql/city_photo_admin.sql in Supabase.',
          );
        }
        throw rpcErr;
      }
      const approved = (approvedRows as { city_id: string; public_url: string }[] | null)?.[0];
      const finalUrl = (approved?.public_url ?? publicUrl).trim();
      if (!finalUrl) throw new Error('Approve succeeded but no public URL was returned');

      // Best-effort cleanup of the hero we just replaced — only Google-fetched objects, never a
      // curated `user-submissions/` one (identical rule to `CityOverviews.approveCityPhoto`).
      const previous = currentHeroUrl?.trim();
      if (previous) {
        try {
          const marker = '/city-heroes/';
          const idx = previous.indexOf(marker);
          if (idx >= 0) {
            const path = previous.slice(idx + marker.length).split('?')[0];
            if (path && !path.startsWith('user-submissions/')) {
              await supabase.storage.from('city-heroes').remove([path]);
            }
          }
        } catch {
          /* non-blocking */
        }
      }

      return finalUrl;
    },
    [adminUserId, cityId, currentHeroUrl],
  );

  /** Use the chosen photo exactly as it is — re-encoded to JPEG so the stored content type is honest. */
  const applyWholePhoto = useCallback(async () => {
    if (!selected?.public_url) return;
    setBusy(true);
    setApplyError(null);
    try {
      const src = await resolveVenuePhotoCropUrl(selected.public_url);
      const { width, height } = await imageNaturalSize(src);
      const blob = await cropImageToJpegBlob(src, { x: 0, y: 0, width, height });
      const heroUrl = await commitHero(blob, selected.id);
      onApplied(heroUrl);
    } catch (e: unknown) {
      setApplyError(e instanceof Error ? e.message : 'Could not set the city photo');
    } finally {
      setBusy(false);
    }
  }, [commitHero, onApplied, selected]);

  /** Open the shared crop modal on a CORS-safe (signed) source. */
  const startCrop = useCallback(async () => {
    if (!selected?.public_url) return;
    setBusy(true);
    setApplyError(null);
    try {
      const src = await resolveVenuePhotoCropUrl(selected.public_url);
      setCropSourceUrl(src);
      setCropOpen(true);
    } catch (e: unknown) {
      setApplyError(e instanceof Error ? e.message : 'Could not open the photo for cropping');
    } finally {
      setBusy(false);
    }
  }, [selected]);

  const confirmCrop = useCallback(
    async (pixelCrop: Area) => {
      if (!selected || !cropSourceUrl) return;
      setBusy(true);
      setApplyError(null);
      try {
        const blob = await cropImageToJpegBlob(cropSourceUrl, pixelCrop);
        const heroUrl = await commitHero(blob, selected.id);
        setCropOpen(false);
        setCropSourceUrl(null);
        onApplied(heroUrl);
      } catch (e: unknown) {
        setApplyError(e instanceof Error ? e.message : 'Could not set the city photo');
      } finally {
        setBusy(false);
      }
    },
    [commitHero, cropSourceUrl, onApplied, selected],
  );

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-label="Choose a city photo"
        onClick={() => !busy && onClose()}
      >
        <div
          className="w-full max-w-3xl rounded-xl border border-border bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <h2 className="admin-label-primary text-base">
                City photo — {cityName?.trim() || 'this city'}
              </h2>
              <p className="mt-1 text-xs text-mutedDim">
                Pick from the {photos.length} approved venue photo{photos.length === 1 ? '' : 's'} in this
                city. The app shows the city hero square, so a venue photo already fits — crop only to
                re-frame it.
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white disabled:opacity-50"
            >
              Close
            </button>
          </div>

          <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
            {applyError ? <ErrorBanner>{applyError}</ErrorBanner> : null}
            {error ? <ErrorBanner>{error}</ErrorBanner> : null}

            <section>
              <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                Current city photo
              </h3>
              <div className="h-32 w-32 overflow-hidden rounded-lg border border-border bg-black">
                {currentHeroUrl?.trim() ? (
                  <img
                    src={currentHeroUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-[11px] text-mutedDim">
                    none
                  </div>
                )}
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                Approved venue photos
              </h3>
              {loading ? (
                <SkeletonRows rows={4} />
              ) : photos.length === 0 ? (
                <EmptyState
                  title="No approved venue photos in this city"
                  hint="Approve a venue photo on the Venue Review page first — only approved photos can become a city hero."
                />
              ) : (
                <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {photos.map((p) => {
                    const active = p.id === selectedId;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          disabled={busy || !p.public_url}
                          aria-pressed={active}
                          onClick={() => setSelectedId(p.id)}
                          className={`block w-full overflow-hidden rounded-lg border text-left transition-colors disabled:opacity-50 ${
                            active
                              ? 'border-accent ring-2 ring-accent'
                              : 'border-border hover:border-white/30'
                          }`}
                        >
                          <div className="aspect-square w-full bg-black">
                            {p.public_url ? (
                              <img
                                src={p.public_url}
                                alt=""
                                referrerPolicy="no-referrer"
                                loading="lazy"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-[10px] text-mutedDim">
                                no url
                              </div>
                            )}
                          </div>
                          <div className="truncate bg-rowAlt px-2 py-1.5 text-[11px] text-muted">
                            {p.venue_name?.trim() || 'Untitled venue'}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-4">
            <span className="mr-auto truncate text-[11px] text-mutedDim">
              {selected ? `Selected: ${selected.venue_name?.trim() || 'Untitled venue'}` : 'Select a photo'}
            </span>
            <button
              type="button"
              disabled={busy || !selected}
              onClick={() => void startCrop()}
              className="admin-btn admin-btn-secondary admin-btn-sm"
            >
              Crop &amp; set
            </button>
            <button
              type="button"
              disabled={busy || !selected}
              onClick={() => void applyWholePhoto()}
              className="admin-btn admin-btn-primary admin-btn-sm"
            >
              {busy ? 'Setting…' : 'Set as city photo'}
            </button>
          </div>
        </div>
      </div>

      {/* Sibling (not a child) of the picker card so its own overlay click can't bubble up and
          close the picker underneath it. Later in DOM order ⇒ paints on top at the same z-50. */}
      {cropOpen && cropSourceUrl ? (
        <VenuePhotoCropModal
          imageUrl={cropSourceUrl}
          title="Crop for the city hero"
          aspect={CITY_HERO_CROP_ASPECT}
          confirmLabel="Set as city photo"
          busyLabel="Setting…"
          busy={busy}
          onCancel={() => {
            if (!busy) {
              setCropOpen(false);
              setCropSourceUrl(null);
            }
          }}
          onConfirm={(pixelCrop) => void confirmCrop(pixelCrop)}
        />
      ) : null}
    </>
  );
}
